/**
 * Formats a number as a currency string according to the user's selected currency.
 *
 * @param {number} amount The numeric amount to format.
 * @param {string} currency The ISO 4217 currency code (e.g., 'EUR', 'USD').
 * @param {Intl.NumberFormatOptions} options Additional options for Intl.NumberFormat.
 * @returns {string} The formatted currency string.
 */
export const formatCurrency = (
    amount: number, 
    currency: string,
    options?: Intl.NumberFormatOptions
): string => {
    // Default options ensure the currency symbol is displayed and sets standard fraction digits.
    const defaultOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    };

    // Combine default and provided options
    const finalOptions = { ...defaultOptions, ...options };

    // Fallback locale 'en' is widely supported. 'es-ES' is a good primary choice.
    const locale = 'es-ES';

    // Handle non-numeric or NaN inputs gracefully.
    if (isNaN(amount) || typeof amount !== 'number') {
        amount = 0;
    }

    try {
        return new Intl.NumberFormat(locale, finalOptions).format(amount);
    } catch (error) {
        console.error(`Error formatting currency for code "${currency}":`, error);
        // Fallback to a simple formatting if Intl fails (e.g., unsupported currency code).
        return `${amount.toFixed(2)} ${currency}`;
    }
};
